import React from 'react';
import moment from 'moment';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { Select } from 'formik-material-ui';
import * as Yup from 'yup';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';

import * as ProfileService from '../../api/profileService';

export interface Values {
  gender: string;
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    color: '#000000',
    marginBottom: theme.spacing(6),
  },
  buttonContainer: {
    width: 100,
    margin: theme.spacing(),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('xs')]: {
      width: 150,
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    display: 'flex',
    paddingLeft: 12,
    paddingRight: 12,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  profile: ProfileService.Profile;
  confirmUpdate: (status: boolean) => void;
};

export default function EditGenderDialog({
  profile,
  onClose,
  open,
  confirmUpdate,
}: Props) {
  const classes = useStyles();
  const ValidationSchema = Yup.object().shape({
    gender: Yup.string().required('Required'),
  });

  const initialValues: Values = {
    gender: !profile.gender ? 'null' : profile.gender,
  };

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>
  ) => {
    if (values.gender && values.gender !== 'null') {
      let dobStr: string | null = null;
      if (profile.dob) {
        dobStr = moment.unix(profile.dob).format('YYYY-MM-DD');
      }
      try {
        await ProfileService.update(
          values.gender,
          dobStr,
          profile.firstName,
          profile.lastName,
          profile.email,
          profile.mobNum,
          profile.alt_email,
          profile.company
        );
        confirmUpdate(true);
        setSubmitting(false);
      } catch (e: any) {
        console.log(e);
        e.data.forEach((err: any) => {
          setFieldError(err.meta.attribute, err.detail);
        });
        setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, dirty, values }) => {
          return (
            <>
              <Form noValidate>
                <AppBar className={classes.appBar} position="sticky">
                  <Toolbar className={classes.toolbar}>
                    <div className={classes.box}>
                      <div className={classes.close}>
                        <IconButton onClick={handleClose}>
                          <CloseIcon className={classes.closeIcon} />
                        </IconButton>
                      </div>
                    </div>
                    <div className={classes.box}>
                      <Typography variant="h6" className={classes.title}>
                        Edit Gender
                      </Typography>
                    </div>
                    <div className={classes.box}>
                      <div className={classes.actions}></div>
                    </div>
                  </Toolbar>
                </AppBar>
                <Container maxWidth="md">
                  <div className={classes.cardContainer}>
                    <Field
                      component={Select}
                      className={classes.textField}
                      name="gender"
                      label="Gender"
                      autoFocus
                      variant="standard"
                    >
                      <MenuItem value={'male'}>Male</MenuItem>
                      <MenuItem value={'female'}>Female</MenuItem>
                      <MenuItem value={'null'}>Not specified</MenuItem>
                    </Field>
                    <div className={classes.actionContainer}>
                      <Button
                        onClick={() => {
                          if (onClose) {
                            onClose({}, 'backdropClick');
                          }
                        }}
                        className={classes.buttonContainer}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        className={classes.buttonContainer}
                        disabled={
                          isSubmitting || !dirty || values.gender === 'null'
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Container>
              </Form>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
}
